import React, { useState, useEffect, useRef } from "react";
import { PlusIcon, XIcon, TrashIcon } from "../icons";
import S from "../styles";

const API_URL = process.env.REACT_APP_API_URL || "https://api.nycmanufacturing.com";

export default function EtsyLabelBotPage() {
  const [labelsFile, setLabelsFile] = useState(null);
  const [detailsFile, setDetailsFile] = useState(null);
  const [draggingLabels, setDraggingLabels] = useState(false);
  const [draggingDetails, setDraggingDetails] = useState(false);
  const [nicknames, setNicknames] = useState([]);
  const [groupings, setGroupings] = useState([]);
  const [newNickListing, setNewNickListing] = useState("");
  const [newNickName, setNewNickName] = useState("");
  const [newGrouping, setNewGrouping] = useState("");
  const [groupByProduct, setGroupByProduct] = useState(true);
  const [showSummaries, setShowSummaries] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [recent, setRecent] = useState([]);
  const [archiveSearch, setArchiveSearch] = useState("");
  const labelsRef = useRef(null);
  const detailsRef = useRef(null);
  const pollRef = useRef(null);

  const currentUser = (() => { try { return JSON.parse(sessionStorage.getItem("nycmfg-user")); } catch { return null; } })();
  const isAdmin = currentUser?.role === "admin";

  const fetchRecent = (q = "") => {
    const url = q.trim()
      ? `${API_URL}/api/label-archives?limit=200&q=${encodeURIComponent(q.trim())}`
      : `${API_URL}/api/label-archives?limit=5`;
    fetch(url).then((r) => r.json()).then((d) => setRecent(Array.isArray(d) ? d : [])).catch(() => {});
  };

  const deleteArchive = (id) => {
    if (!window.confirm("Delete this label combination? It will be removed from the list and from Google Drive.")) return;
    fetch(`${API_URL}/api/label-archive/${id}?empId=${encodeURIComponent(currentUser?.empId || "")}`, { method: "DELETE" })
      .then(async (r) => { if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || `Server ${r.status}`); } })
      .then(() => fetchRecent(archiveSearch))
      .catch((e) => window.alert(`Delete failed: ${e.message}`));
  };

  // Fetch nicknames, groupings, and recent combinations on mount
  useEffect(() => {
    fetch(`${API_URL}/api/label-nicknames`).then((r) => r.json()).then(setNicknames).catch(() => {});
    fetch(`${API_URL}/api/label-groupings`).then((r) => r.json()).then(setGroupings).catch(() => {});
    fetchRecent();
  }, []);

  const addNickname = () => {
    if (!newNickListing.trim() || !newNickName.trim()) return;
    fetch(`${API_URL}/api/label-nicknames`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ listing: newNickListing.trim(), nickname: newNickName.trim() }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.error) { setError(data.error); return; }
        setNicknames([...nicknames, data]);
        setNewNickListing("");
        setNewNickName("");
      })
      .catch(() => {});
  };

  const deleteNickname = (id) => {
    fetch(`${API_URL}/api/label-nicknames/${id}`, { method: "DELETE" })
      .then(() => setNicknames(nicknames.filter((n) => n.id !== id)))
      .catch(() => {});
  };

  const addGrouping = () => {
    if (!newGrouping.trim()) return;
    fetch(`${API_URL}/api/label-groupings`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: newGrouping.trim() }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.error) { setError(data.error); return; }
        setGroupings([...groupings, data]);
        setNewGrouping("");
      })
      .catch(() => {});
  };

  const deleteGrouping = (id) => {
    fetch(`${API_URL}/api/label-groupings/${id}`, { method: "DELETE" })
      .then(() => setGroupings(groupings.filter((g) => g.id !== id)))
      .catch(() => {});
  };

  // Clean up the progress poller if the page unmounts mid-run
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const handleProcess = async () => {
    if (!labelsFile || !detailsFile) return;
    setProcessing(true);
    setResult(null);
    setError(null);
    setPdfUrl(null);
    setProgress({ current: 0, total: 0, stage: "Starting" });

    const formData = new FormData();
    formData.append("labels", labelsFile);
    formData.append("details", detailsFile);
    formData.append("group_by_product", groupByProduct);
    formData.append("show_summaries", showSummaries);

    // Poll progress while the (blocking) process request runs
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await fetch(`${API_URL}/api/label-progress?t=${Date.now()}`);
        const p = await r.json();
        if (!p.done) setProgress(p);
      } catch (e) { /* ignore transient poll errors */ }
    }, 800);

    try {
      const res = await fetch(`${API_URL}/api/label-process`, { method: "POST", body: formData });
      const data = await res.json();
      if (data.error) {
        setError(data.error);
      } else {
        setResult(data.stats);
        setPdfUrl(`${API_URL}/api/label-view?t=${Date.now()}`);
        fetchRecent(archiveSearch);
      }
    } catch (e) {
      setError("Failed to process PDFs. Please try again.");
    }
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setProgress(null);
    setProcessing(false);
  };

  const handleDownload = () => {
    window.open(`${API_URL}/api/label-download?t=${Date.now()}`, "_blank");
  };

  const dropHandler = (e, setter, dragSetter) => {
    e.preventDefault();
    dragSetter(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type === "application/pdf") setter(file);
  };

  const fileBox = (label, file, setter, dragging, dragSetter, inputRef) => (
    <div
      onDragOver={(e) => { e.preventDefault(); dragSetter(true); }}
      onDragEnter={(e) => { e.preventDefault(); dragSetter(true); }}
      onDragLeave={() => dragSetter(false)}
      onDrop={(e) => dropHandler(e, setter, dragSetter)}
      onClick={() => inputRef.current && inputRef.current.click()}
      style={{
        flex: 1, minWidth: 250, border: `2px dashed ${dragging ? "#0033FF" : "#0A0A0F"}`,
        borderRadius: 0, padding: 30, textAlign: "center", cursor: "pointer",
        background: dragging ? "rgba(200,149,108,0.06)" : file ? "rgba(34,197,94,0.06)" : "#FFFFFF",
        transition: "all .2s",
      }}
    >
      {file ? (
        <div>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 8 }}>
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /><polyline points="9 15 12 18 15 15" />
          </svg>
          <div style={{ fontSize: 14, color: "#22c55e", fontWeight: 600, marginBottom: 4 }}>{file.name}</div>
          <div style={{ fontSize: 12, color: "rgba(10,10,15,0.58)" }}>{(file.size / 1024).toFixed(0)} KB</div>
          <button
            onClick={(e) => { e.stopPropagation(); setter(null); }}
            style={{ ...S.clearBtn, marginTop: 8, fontSize: 12, padding: "4px 10px" }}
          >
            Remove
          </button>
        </div>
      ) : (
        <div>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={dragging ? "#0033FF" : "rgba(10,10,15,0.58)"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 8 }}>
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" />
            <line x1="12" y1="18" x2="12" y2="12" /><line x1="9" y1="15" x2="12" y2="12" /><line x1="15" y1="15" x2="12" y2="12" />
          </svg>
          <div style={{ fontSize: 15, color: dragging ? "#0033FF" : "rgba(10,10,15,0.58)", fontWeight: 600, marginBottom: 4 }}>{label}</div>
          <div style={{ fontSize: 12, color: "rgba(10,10,15,0.58)" }}>Drop PDF or click to browse</div>
        </div>
      )}
    </div>
  );

  return (
    <div>
      <div style={{ marginBottom: 4 }}>
        <h2 style={{ fontFamily: "'Archivo Black', 'Archivo', sans-serif", fontSize: 32, fontWeight: 900, color: "#0A0A0F", letterSpacing: "-0.02em", textTransform: "uppercase", lineHeight: 1, marginBottom: 4 }}>Etsy Label Bot</h2>
        <p style={{ fontSize: 14, color: "rgba(10,10,15,0.58)", marginBottom: 20 }}>Upload shipping labels + order details to auto-match and combine with customer info pages</p>
      </div>

      {/* Error banner */}
      {error && (
        <div style={{ background: "rgba(239,68,68,0.1)", border: "1px solid #ef4444", borderRadius: 0, padding: "12px 16px", marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ color: "#ef4444", fontSize: 14 }}>{error}</span>
          <button onClick={() => setError(null)} style={{ ...S.iconBtn, borderColor: "#ef4444", color: "#ef4444" }}><XIcon /></button>
        </div>
      )}

      {/* File Upload */}
      <div style={{ display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
        {fileBox("Shipping Labels PDF", labelsFile, setLabelsFile, draggingLabels, setDraggingLabels, labelsRef)}
        {fileBox("Order Details PDF", detailsFile, setDetailsFile, draggingDetails, setDraggingDetails, detailsRef)}
        <input ref={labelsRef} type="file" accept=".pdf" onChange={(e) => { if (e.target.files[0]) setLabelsFile(e.target.files[0]); e.target.value = ""; }} style={{ display: "none" }} />
        <input ref={detailsRef} type="file" accept=".pdf" onChange={(e) => { if (e.target.files[0]) setDetailsFile(e.target.files[0]); e.target.value = ""; }} style={{ display: "none" }} />
      </div>

      {/* Options + Process */}
      <div style={{ background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0, padding: 20, marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
          <div style={{ display: "flex", gap: 20 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 14, color: "#0A0A0F" }}>
              <input type="checkbox" checked={groupByProduct} onChange={(e) => setGroupByProduct(e.target.checked)} style={{ accentColor: "#0033FF" }} />
              Group by product
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 14, color: "#0A0A0F" }}>
              <input type="checkbox" checked={showSummaries} onChange={(e) => setShowSummaries(e.target.checked)} style={{ accentColor: "#0033FF" }} />
              Show summary slips
            </label>
          </div>
          <button
            onClick={handleProcess}
            disabled={!labelsFile || !detailsFile || processing}
            style={{
              padding: "10px 24px", borderRadius: 0, fontSize: 15, fontWeight: 600,
              fontFamily: "'Archivo', sans-serif", cursor: processing ? "wait" : (!labelsFile || !detailsFile ? "not-allowed" : "pointer"),
              border: !labelsFile || !detailsFile ? "2px solid rgba(10,10,15,0.25)" : "none",
              background: !labelsFile || !detailsFile ? "rgba(10,10,15,0.06)" : "#0033FF",
              color: !labelsFile || !detailsFile ? "rgba(10,10,15,0.45)" : "#FFFFFF",
              opacity: processing ? 0.6 : 1, transition: "all .15s",
            }}
          >
            {processing ? "Processing..." : "Process PDFs"}
          </button>
        </div>
      </div>

      {/* Progress */}
      {processing && progress && (() => {
        const total = progress.total || 0;
        const current = progress.current || 0;
        const pct = total > 0 ? Math.min(100, Math.round((current / total) * 100)) : null;
        return (
          <div style={{ background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0, padding: 20, marginBottom: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: "#0A0A0F" }}>
                {progress.stage === "Combining PDF" ? "Combining PDF…"
                  : total > 0 ? `Reading labels — ${current} / ${total}`
                  : "Starting…"}
              </span>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 14, fontWeight: 700, color: "#0033FF" }}>
                {pct === null ? "" : `${pct}%`}
              </span>
            </div>
            <div style={{ height: 14, background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0, overflow: "hidden" }}>
              <div style={{
                height: "100%",
                width: pct === null ? "15%" : `${pct}%`,
                background: "#0033FF",
                transition: "width .3s ease",
                opacity: pct === null ? 0.5 : 1,
              }} />
            </div>
          </div>
        );
      })()}

      {/* Results */}
      {result && (
        <div style={{ background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0, padding: 20, marginBottom: 24 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: "#0A0A0F", marginBottom: 12 }}>Results</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12, marginBottom: 16 }}>
            <div style={S.statCard}><span style={S.statLabel}>Labels</span><span style={S.statValue}>{result.total_labels}</span></div>
            <div style={S.statCard}><span style={S.statLabel}>Orders</span><span style={S.statValue}>{result.total_orders}</span></div>
            <div style={S.statCard}><span style={S.statLabel}>Matched</span><span style={{ ...S.statValue, color: "#34d399" }}>{result.matched_count}</span></div>
            <div style={S.statCard}><span style={S.statLabel}>Unmatched</span><span style={{ ...S.statValue, color: result.unmatched_labels?.length > 0 ? "#ef4444" : "#34d399" }}>{(result.unmatched_labels?.length || 0) + (result.unmatched_orders?.length || 0)}</span></div>
          </div>

          {/* Unmatched details */}
          {result.unmatched_labels?.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#ef4444", marginBottom: 6 }}>Unmatched Labels:</div>
              {result.unmatched_labels.map((u, i) => (
                <div key={i} style={{ fontSize: 13, color: "#0A0A0F", paddingLeft: 12 }}>Label #{u.label_index}: {u.extracted_name}</div>
              ))}
            </div>
          )}
          {result.unmatched_orders?.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#0033FF", marginBottom: 6 }}>Orders Without Labels:</div>
              {result.unmatched_orders.map((u, i) => (
                <div key={i} style={{ fontSize: 13, color: "#0A0A0F", paddingLeft: 12 }}>{u.name}</div>
              ))}
            </div>
          )}

          <button onClick={handleDownload} style={{
            padding: "10px 24px", borderRadius: 0, border: "none", fontSize: 15, fontWeight: 600,
            fontFamily: "'Archivo', sans-serif", cursor: "pointer",
            background: "#0033FF", color: "#FFFFFF",
          }}>
            Download Combined PDF
          </button>
        </div>
      )}

      {/* PDF Preview */}
      {pdfUrl && (
        <div style={{ background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0, overflow: "hidden", marginBottom: 24 }}>
          <div style={{ padding: "12px 16px", borderBottom: "2px solid #0A0A0F" }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: "#0A0A0F" }}>Preview</span>
          </div>
          <iframe src={pdfUrl} title="PDF Preview" style={{ width: "100%", height: 600, border: "none", background: "#fff" }} />
        </div>
      )}

      {/* Nicknames + Groupings side by side */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {/* Nicknames */}
        <div style={{ background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0, padding: 20 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: "#0A0A0F", marginBottom: 4 }}>Product Nicknames</h3>
          <p style={{ fontSize: 12, color: "rgba(10,10,15,0.58)", marginBottom: 12 }}>Shorten long Etsy listing names for info pages</p>

          <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
            <input value={newNickListing} onChange={(e) => setNewNickListing(e.target.value)} placeholder="Full listing name" style={{ ...S.input, marginBottom: 0, flex: 2, fontSize: 13, minWidth: 120 }} />
            <input value={newNickName} onChange={(e) => setNewNickName(e.target.value)} placeholder="Nickname"
              onKeyDown={(e) => { if (e.key === "Enter") addNickname(); }}
              style={{ ...S.input, marginBottom: 0, flex: 1, fontSize: 13, minWidth: 80 }} />
            <button onClick={addNickname} style={{ ...S.iconBtn, borderColor: "#22c55e", color: "#22c55e" }}><PlusIcon /></button>
          </div>

          {nicknames.length === 0 ? (
            <div style={{ fontSize: 13, color: "rgba(10,10,15,0.58)", textAlign: "center", padding: 16 }}>No nicknames yet</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {nicknames.map((n) => (
                <div key={n.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#FFFFFF", borderRadius: 0, padding: "8px 12px", border: "2px solid #0A0A0F" }}>
                  <div style={{ flex: 1, overflow: "hidden" }}>
                    <div style={{ fontSize: 13, color: "rgba(10,10,15,0.58)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{n.listing}</div>
                    <div style={{ fontSize: 14, color: "#0033FF", fontWeight: 600 }}>{n.nickname}</div>
                  </div>
                  <button onClick={() => deleteNickname(n.id)} style={{ ...S.iconBtn, marginLeft: 8 }}><TrashIcon /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Groupings */}
        <div style={{ background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0, padding: 20 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: "#0A0A0F", marginBottom: 4 }}>Variation Groupings</h3>
          <p style={{ fontSize: 12, color: "rgba(10,10,15,0.58)", marginBottom: 12 }}>Products that show variation breakdowns in summaries</p>

          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <input value={newGrouping} onChange={(e) => setNewGrouping(e.target.value)} placeholder="Product name"
              onKeyDown={(e) => { if (e.key === "Enter") addGrouping(); }}
              style={{ ...S.input, marginBottom: 0, flex: 1, fontSize: 13 }} />
            <button onClick={addGrouping} style={{ ...S.iconBtn, borderColor: "#22c55e", color: "#22c55e" }}><PlusIcon /></button>
          </div>

          {groupings.length === 0 ? (
            <div style={{ fontSize: 13, color: "rgba(10,10,15,0.58)", textAlign: "center", padding: 16 }}>No groupings yet</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {groupings.map((g) => (
                <div key={g.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#FFFFFF", borderRadius: 0, padding: "8px 12px", border: "2px solid #0A0A0F" }}>
                  <span style={{ fontSize: 14, color: "#0A0A0F" }}>{g.name}</span>
                  <button onClick={() => deleteGrouping(g.id)} style={S.iconBtn}><TrashIcon /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent Combinations */}
      <div style={{ background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0, padding: 20, marginTop: 16 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: "#0A0A0F", marginBottom: 4 }}>Label Combinations</h3>
        <p style={{ fontSize: 12, color: "rgba(10,10,15,0.58)", marginBottom: 12 }}>
          Last 5 shown. Every combination is archived to Google Drive — search by date (e.g. <code style={{ fontFamily: "'JetBrains Mono', monospace" }}>2026-06-17</code>) to find an older one to download &amp; reprint.
        </p>
        <input
          value={archiveSearch}
          onChange={(e) => { setArchiveSearch(e.target.value); fetchRecent(e.target.value); }}
          placeholder="Search past combinations by date…"
          style={{ ...S.input, marginBottom: 12 }}
        />
        {recent.length === 0 ? (
          <div style={{ fontSize: 13, color: "rgba(10,10,15,0.58)", textAlign: "center", padding: 16 }}>
            {archiveSearch.trim() ? "No combinations match that search" : "No combinations yet"}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {recent.map((a) => (
              <div key={a.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, background: "#FFFFFF", borderRadius: 0, padding: "10px 12px", border: "2px solid #0A0A0F" }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#0A0A0F", fontFamily: "'JetBrains Mono', monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.filename}</div>
                  <div style={{ fontSize: 12, color: "rgba(10,10,15,0.58)" }}>
                    {a.createdAt ? new Date(a.createdAt).toLocaleString() : ""} · {a.totalLabels} labels{a.matchedCount ? ` · ${a.matchedCount} matched` : ""}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 10, alignItems: "center", flexShrink: 0 }}>
                  {a.driveLink && (
                    <a href={a.driveLink} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, fontWeight: 700, color: "#0033FF", textDecoration: "none" }}>Drive ↗</a>
                  )}
                  {a.hasLocal ? (
                    <button onClick={() => window.open(`${API_URL}/api/label-archive/${a.id}/download`, "_blank")} style={{ ...S.clearBtn, fontSize: 12, padding: "6px 12px" }}>Download</button>
                  ) : (
                    <span style={{ fontSize: 11, color: "rgba(10,10,15,0.45)" }}>Drive only</span>
                  )}
                  {isAdmin && (
                    <button onClick={() => deleteArchive(a.id)} title="Delete (admin)" style={{ ...S.iconBtn, padding: "6px 8px", borderColor: "#ef4444", color: "#ef4444" }}><TrashIcon /></button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
